/**
 * Hardware-Accelerated Animation Framework
 * 60fps Performance with GPU Optimization and Advanced Animation Management
 */

import {
  GPUAnimationProperties,
  AnimationTimingConfig,
  AnimationPerformanceMetrics,
  AnimationPool,
  AnimationBatch,
  AnimationRequest,
  ReducedMotionConfig,
  AnimationConfig,
} from "./types";

/**
 * Core Hardware-Accelerated Animation Engine
 * Manages GPU-optimized animations with performance monitoring
 */
export class HardwareAcceleratedAnimationFramework {
  private performanceMetrics: AnimationPerformanceMetrics;
  private animationPools: Map<string, AnimationPool> = new Map();
  private batchQueue: AnimationBatch[] = [];
  private activeAnimations: Map<string, Animation> = new Map();
  private reducedMotionConfig: ReducedMotionConfig;
  private isReducedMotionEnabled: boolean = false;

  // Performance monitoring
  private frameRateMonitor: PerformanceFrameMonitor;
  private memoryMonitor: MemoryUsageMonitor;

  constructor(config?: Partial<ReducedMotionConfig>) {
    this.reducedMotionConfig = {
      enabled: true,
      respectSystemPreferences: true,
      fallbackAnimations: new Map(),
      staticAlternatives: new Map(),
      minimumDuration: 50,
      ...config,
    };

    this.performanceMetrics = {
      frameRate: 60,
      frameDropCount: 0,
      animationLatency: 0,
      memoryUsage: 0,
      gpuLayersActive: 0,
      reducedMotionActive: false,
    };

    this.frameRateMonitor = new PerformanceFrameMonitor();
    this.memoryMonitor = new MemoryUsageMonitor();

    this.initializeFramework();
  }

  /**
   * Initialize the animation framework with performance monitoring
   */
  private initializeFramework(): void {
    // Check for reduced motion preference
    this.checkReducedMotionPreference();

    // Initialize performance monitoring
    this.startPerformanceMonitoring();

    // Setup animation pools
    this.initializeAnimationPools();

    // Setup batch processing
    this.initializeBatchProcessor();
  }

  /**
   * Check system reduced motion preferences
   */
  private checkReducedMotionPreference(): void {
    if (this.reducedMotionConfig.respectSystemPreferences) {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.isReducedMotionEnabled = mediaQuery.matches;

      mediaQuery.addEventListener("change", (e) => {
        this.isReducedMotionEnabled = e.matches;
        this.performanceMetrics.reducedMotionActive = e.matches;
        this.handleReducedMotionChange();
      });
    }
  }

  /**
   * Handle reduced motion preference changes
   */
  private handleReducedMotionChange(): void {
    if (this.isReducedMotionEnabled) {
      // Convert running animations to reduced motion versions
      this.activeAnimations.forEach((animation, id) => {
        const element = document.querySelector(
          `[data-animation-id="${id}"]`,
        ) as HTMLElement;
        if (element) {
          this.convertToReducedMotion(animation, element);
        }
      });
    }
  }

  /**
   * Convert animation to reduced motion version
   */
  private convertToReducedMotion(
    animation: Animation,
    element: HTMLElement,
  ): void {
    // Cancel current animation
    animation.cancel();

    // Apply final state instantly or with minimal animation
    if (animation.effect instanceof KeyframeEffect) {
      const keyframes = animation.effect.getKeyframes();
      if (keyframes.length > 0) {
        const finalKeyframe = keyframes[keyframes.length - 1];

        // Apply final styles with short transition
        Object.assign(element.style, finalKeyframe);
        element.style.transition = `all ${this.reducedMotionConfig.minimumDuration}ms ease`;
      }
    }
  }

  /**
   * Create hardware-accelerated animation with agricultural context awareness
   */
  public createAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: {
      id?: string;
      priority?: "high" | "normal" | "low";
      agriculturalMode?: boolean;
    } = {},
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const animationId =
          options.id ||
          `anim-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Apply will-change optimization
        this.optimizeForHardwareAcceleration(element, properties);

        // Apply agricultural context optimizations if enabled
        const optimizedProperties = options.agriculturalMode
          ? this.applyAgriculturalOptimizations(properties)
          : properties;
        const optimizedTiming = options.agriculturalMode
          ? this.applyAgriculturalTimingOptimizations(timing)
          : timing;

        // Create keyframes for GPU-optimized properties
        const keyframes = this.createGPUOptimizedKeyframes(
          optimizedProperties,
          optimizedTiming,
        );

        // Create animation configuration
        const animationOptions: KeyframeAnimationOptions = {
          duration: this.isReducedMotionEnabled
            ? Math.min(
                timing.duration,
                this.reducedMotionConfig.minimumDuration,
              )
            : timing.duration,
          easing: timing.easing,
          delay: timing.delay,
          iterations: this.isReducedMotionEnabled ? 1 : timing.iterations,
          direction: timing.direction,
          fill: timing.fillMode,
        };

        // Check if we should use reduced motion
        if (this.isReducedMotionEnabled) {
          const reducedKeyframes = this.createReducedMotionKeyframes(keyframes);
          this.createWebAnimation(
            element,
            reducedKeyframes,
            animationOptions,
            animationId,
          )
            .then(resolve)
            .catch(reject);
          return;
        }

        // Create full animation
        this.createWebAnimation(
          element,
          keyframes,
          animationOptions,
          animationId,
        )
          .then(resolve)
          .catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Optimize element for hardware acceleration
   */
  private optimizeForHardwareAcceleration(
    element: HTMLElement,
    properties: GPUAnimationProperties,
  ): void {
    // Force GPU layer creation
    element.style.willChange = properties.willChange.join(", ");
    element.style.backfaceVisibility = properties.backfaceVisibility;

    // Ensure element is on its own composite layer
    if (!element.style.transform.includes("translateZ")) {
      element.style.transform =
        `${element.style.transform} translateZ(0)`.trim();
    }

    // Set perspective for 3D context
    if (properties.perspective > 0) {
      element.style.perspective = `${properties.perspective}px`;
    }

    this.performanceMetrics.gpuLayersActive++;
  }

  /**
   * Create GPU-optimized keyframes
   */
  private createGPUOptimizedKeyframes(
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
  ): Keyframe[] {
    const keyframes: Keyframe[] = [];

    // Start keyframe (from current state)
    keyframes.push({
      transform: this.getCurrentTransform(properties),
      opacity: this.getCurrentOpacity(properties),
      offset: 0,
    });

    // End keyframe (target state)
    keyframes.push({
      transform: properties.transform,
      opacity: properties.opacity,
      offset: 1,
    });

    // Add intermediate keyframes for complex animations
    if (timing.easing.includes("bounce") || timing.easing.includes("elastic")) {
      keyframes.splice(
        1,
        0,
        ...this.createIntermediateKeyframes(properties, timing),
      );
    }

    return keyframes;
  }

  /**
   * Create intermediate keyframes for complex animations
   */
  private createIntermediateKeyframes(
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
  ): Keyframe[] {
    const intermediates: Keyframe[] = [];

    if (timing.easing.includes("bounce")) {
      // Create bounce keyframes
      intermediates.push(
        {
          transform: this.interpolateTransform(properties, 0.7, 1.1),
          opacity: this.interpolateOpacity(properties, 0.7),
          offset: 0.7,
        },
        {
          transform: this.interpolateTransform(properties, 0.85, 0.95),
          opacity: this.interpolateOpacity(properties, 0.85),
          offset: 0.85,
        },
      );
    }

    return intermediates;
  }

  /**
   * Create Web Animation API animation
   */
  private createWebAnimation(
    element: HTMLElement,
    keyframes: Keyframe[],
    options: KeyframeAnimationOptions,
    animationId: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const animation = element.animate(keyframes, options);

        // Store animation reference
        this.activeAnimations.set(animationId, animation);
        element.setAttribute("data-animation-id", animationId);

        // Performance monitoring
        const startTime = performance.now();
        this.frameRateMonitor.startMonitoring(animationId);

        animation.addEventListener("finish", () => {
          this.handleAnimationComplete(animationId, element, startTime);
          resolve();
        });

        animation.addEventListener("cancel", () => {
          this.handleAnimationCancel(animationId, element);
          resolve();
        });

        animation.addEventListener("error", (error) => {
          this.handleAnimationError(animationId, element, error);
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Handle animation completion
   */
  private handleAnimationComplete(
    animationId: string,
    element: HTMLElement,
    startTime: number,
  ): void {
    // Calculate performance metrics
    const endTime = performance.now();
    this.performanceMetrics.animationLatency = endTime - startTime;

    // Clean up animation
    this.activeAnimations.delete(animationId);
    element.removeAttribute("data-animation-id");

    // Clean up will-change optimization
    this.cleanupHardwareAcceleration(element);

    // Stop performance monitoring
    this.frameRateMonitor.stopMonitoring(animationId);

    this.performanceMetrics.gpuLayersActive--;
  }

  /**
   * Handle animation cancellation
   */
  private handleAnimationCancel(
    animationId: string,
    element: HTMLElement,
  ): void {
    this.activeAnimations.delete(animationId);
    element.removeAttribute("data-animation-id");
    this.cleanupHardwareAcceleration(element);
    this.frameRateMonitor.stopMonitoring(animationId);
    this.performanceMetrics.gpuLayersActive--;
  }

  /**
   * Handle animation error
   */
  private handleAnimationError(
    animationId: string,
    element: HTMLElement,
    error: any,
  ): void {
    console.error(`Animation ${animationId} failed:`, error);
    this.handleAnimationCancel(animationId, element);
  }

  /**
   * Clean up hardware acceleration optimizations
   */
  private cleanupHardwareAcceleration(element: HTMLElement): void {
    // Reset will-change property
    element.style.willChange = "auto";

    // Remove translateZ(0) if it was added for layer promotion
    if (element.style.transform.includes("translateZ(0)")) {
      element.style.transform = element.style.transform
        .replace("translateZ(0)", "")
        .trim();
    }
  }

  /**
   * Batch animation processing for performance
   */
  public processBatchAnimations(requests: AnimationRequest[]): Promise<void[]> {
    const batch: AnimationBatch = {
      id: `batch-${Date.now()}`,
      animations: requests,
      priority: "normal",
      batchSize: requests.length,
      processed: false,
    };

    this.batchQueue.push(batch);

    return this.processBatch(batch);
  }

  /**
   * Process animation batch
   */
  private processBatch(batch: AnimationBatch): Promise<void[]> {
    return new Promise((resolve) => {
      // Use requestAnimationFrame for optimal timing
      requestAnimationFrame(() => {
        const promises = batch.animations.map((request) => {
          const element = document.getElementById(request.elementId);
          if (element) {
            return this.createAnimation(
              element,
              request.properties,
              request.timing,
            );
          }
          return Promise.resolve();
        });

        batch.processed = true;
        Promise.all(promises).then(resolve);
      });
    });
  }

  /**
   * Animation pooling for memory efficiency
   */
  private initializeAnimationPools(): void {
    const poolTypes = ["bounce", "slide", "fade", "scale", "rotate"];

    poolTypes.forEach((type) => {
      this.animationPools.set(type, {
        id: type,
        animations: new Map(),
        maxPoolSize: 50,
        recycledAnimations: [],
        activeAnimations: new Set(),
      });
    });
  }

  /**
   * Initialize batch processor
   */
  private initializeBatchProcessor(): void {
    // Process batches every frame for optimal performance
    const processBatches = () => {
      if (this.batchQueue.length > 0) {
        const batch = this.batchQueue.shift();
        if (batch && !batch.processed) {
          this.processBatch(batch);
        }
      }
      requestAnimationFrame(processBatches);
    };

    processBatches();
  }

  /**
   * Start performance monitoring
   */
  private startPerformanceMonitoring(): void {
    this.frameRateMonitor.onFrameDropDetected = (drops) => {
      this.performanceMetrics.frameDropCount = drops;

      // Auto-adjust performance if too many drops
      if (drops > 10) {
        this.enablePerformanceMode();
      }
    };

    this.memoryMonitor.onMemoryThresholdExceeded = (usage) => {
      this.performanceMetrics.memoryUsage = usage;
      this.cleanupUnusedAnimations();
    };

    // Monitor every second
    setInterval(() => {
      this.updatePerformanceMetrics();
    }, 1000);
  }

  /**
   * Enable performance mode when frame drops detected
   */
  private enablePerformanceMode(): void {
    // Reduce animation quality
    this.activeAnimations.forEach((animation, id) => {
      if (animation.playbackRate !== 1.5) {
        animation.playbackRate = 1.5; // Speed up animations
      }
    });

    // Clear non-essential animations
    this.cleanupNonEssentialAnimations();
  }

  /**
   * Cleanup unused animations to free memory
   */
  private cleanupUnusedAnimations(): void {
    this.animationPools.forEach((pool) => {
      // Keep only half the recycled animations
      pool.recycledAnimations = pool.recycledAnimations.slice(
        0,
        pool.recycledAnimations.length / 2,
      );
    });
  }

  /**
   * Cleanup non-essential animations during performance issues
   */
  private cleanupNonEssentialAnimations(): void {
    const nonEssentialTypes = ["decoration", "ambient", "subtle"];

    this.activeAnimations.forEach((animation, id) => {
      const element = document.querySelector(`[data-animation-id="${id}"]`);
      const animationType = element?.getAttribute("data-animation-type");

      if (animationType && nonEssentialTypes.includes(animationType)) {
        animation.cancel();
      }
    });
  }

  /**
   * Update performance metrics
   */
  private updatePerformanceMetrics(): void {
    this.performanceMetrics = {
      ...this.performanceMetrics,
      frameRate: this.frameRateMonitor.getCurrentFrameRate(),
      memoryUsage: this.memoryMonitor.getCurrentUsage(),
      reducedMotionActive: this.isReducedMotionEnabled,
    };
  }

  // Utility methods for keyframe creation
  private getCurrentTransform(properties: GPUAnimationProperties): string {
    return "translate3d(0, 0, 0) scale(1) rotate(0deg)";
  }

  private getCurrentOpacity(properties: GPUAnimationProperties): number {
    return 1;
  }

  private interpolateTransform(
    properties: GPUAnimationProperties,
    progress: number,
    scaleMultiplier: number = 1,
  ): string {
    // Extract transform values and interpolate
    const scale = scaleMultiplier;
    return `translate3d(0, 0, 0) scale(${scale}) rotate(0deg)`;
  }

  private interpolateOpacity(
    properties: GPUAnimationProperties,
    progress: number,
  ): number {
    return Math.min(1, progress + 0.1);
  }

  private createReducedMotionKeyframes(
    originalKeyframes: Keyframe[],
  ): Keyframe[] {
    // Return only start and end keyframes for reduced motion
    return [
      originalKeyframes[0],
      originalKeyframes[originalKeyframes.length - 1],
    ];
  }

  /**
   * Public API methods
   */
  public getPerformanceMetrics(): AnimationPerformanceMetrics {
    return { ...this.performanceMetrics };
  }

  public cancelAnimation(animationId: string): void {
    const animation = this.activeAnimations.get(animationId);
    if (animation) {
      animation.cancel();
    }
  }

  public cancelAllAnimations(): void {
    this.activeAnimations.forEach((animation) => animation.cancel());
    this.activeAnimations.clear();
  }

  public setReducedMotionEnabled(enabled: boolean): void {
    this.isReducedMotionEnabled = enabled;
    this.performanceMetrics.reducedMotionActive = enabled;
    this.handleReducedMotionChange();
  }

  /**
   * Apply agricultural context optimizations to animation properties
   */
  private applyAgriculturalOptimizations(
    properties: GPUAnimationProperties,
  ): GPUAnimationProperties {
    return {
      ...properties,
      // Reduce rotation for clearer visual feedback in outdoor conditions
      transform: properties.transform.replace(
        /rotate\([^)]+\)/g,
        "rotate(0deg)",
      ),
      // Increase contrast for outdoor visibility
      opacity: Math.min(1, properties.opacity * 1.1),
      // Optimize for touch/glove interaction
      willChange: [...properties.willChange, "transform", "opacity", "filter"],
      backfaceVisibility: "hidden", // Ensure consistent rendering
      perspective: properties.perspective * 1.2, // Enhance 3D depth perception
    };
  }

  /**
   * Apply agricultural context timing optimizations
   */
  private applyAgriculturalTimingOptimizations(
    timing: AnimationTimingConfig,
  ): AnimationTimingConfig {
    return {
      ...timing,
      // Slightly longer duration for better visibility in outdoor conditions
      duration: Math.min(timing.duration * 1.2, 500),
      // Simpler easing for clarity
      easing:
        timing.easing.includes("bounce") || timing.easing.includes("elastic")
          ? "ease-out"
          : timing.easing,
      // Reduce delay for more responsive feel with gloves
      delay: Math.max(0, timing.delay * 0.8),
    };
  }

  /**
   * Create agricultural-optimized animation with enhanced visibility
   */
  public createAgriculturalAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: {
      id?: string;
      priority?: "high" | "normal" | "low";
      outdoorMode?: boolean;
    } = {},
  ): Promise<void> {
    // Apply agricultural-specific optimizations
    const agriculturalProperties =
      this.applyAgriculturalOptimizations(properties);
    const agriculturalTiming =
      this.applyAgriculturalTimingOptimizations(timing);

    // Apply outdoor mode enhancements if specified
    if (options.outdoorMode) {
      element.style.filter = "brightness(1.2) contrast(1.1) saturate(1.05)";
      element.style.textShadow = "1px 1px 2px rgba(0, 0, 0, 0.8)";
    }

    return this.createAnimation(
      element,
      agriculturalProperties,
      agriculturalTiming,
      {
        ...options,
        agriculturalMode: true,
      },
    );
  }

  /**
   * Create high-visibility animation for weather-resistant mode
   */
  public createWeatherResistantAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: { id?: string; priority?: "high" | "normal" | "low" } = {},
  ): Promise<void> {
    // Apply extreme visibility optimizations
    const weatherProperties: GPUAnimationProperties = {
      ...properties,
      transform: properties.transform.replace(
        /rotate\([^)]+\)/g,
        "rotate(0deg)",
      ),
      opacity: 1, // Full opacity for maximum visibility
      willChange: ["transform", "opacity", "filter", "box-shadow"],
      backfaceVisibility: "hidden",
      perspective: 1000,
    };

    const weatherTiming: AnimationTimingConfig = {
      ...timing,
      duration: Math.min(timing.duration * 1.5, 600), // Longer for clarity
      easing: "ease-out", // Simple easing
      delay: 0, // No delay for immediate feedback
    };

    // Apply weather-resistant styling
    element.style.filter = "brightness(1.3) contrast(1.3) saturate(1.2)";
    element.style.boxShadow = "0 0 16px rgba(255, 255, 0, 0.8)";
    element.style.border = "3px solid #FFFF00";

    return this.createAnimation(
      element,
      weatherProperties,
      weatherTiming,
      options,
    );
  }

  /**
   * Auto-detect agricultural context and apply appropriate optimizations
   */
  public autoDetectAgriculturalContext(): boolean {
    // Detect touch device (common in agricultural settings)
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // Detect high contrast preference (outdoor indicator)
    const prefersHighContrast =
      window.matchMedia &&
      window.matchMedia("(prefers-contrast: high)").matches;

    // Detect reduced transparency (another outdoor indicator)
    const prefersReducedTransparency =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-transparency: reduce)").matches;

    // Return true if any agricultural context indicators are present
    return isTouchDevice || prefersHighContrast || prefersReducedTransparency;
  }

  public dispose(): void {
    this.cancelAllAnimations();
    this.frameRateMonitor.dispose();
    this.memoryMonitor.dispose();
    this.animationPools.clear();
    this.batchQueue.length = 0;
  }
}

/**
 * Performance monitoring classes
 */
class PerformanceFrameMonitor {
  private frameCount: number = 0;
  private lastFrameTime: number = performance.now();
  private frameDropThreshold: number = 16.67; // 60fps = 16.67ms per frame
  private activeMonitors: Map<string, number> = new Map();

  public onFrameDropDetected?: (drops: number) => void;

  public startMonitoring(animationId: string): void {
    this.activeMonitors.set(animationId, performance.now());
  }

  public stopMonitoring(animationId: string): void {
    this.activeMonitors.delete(animationId);
  }

  public getCurrentFrameRate(): number {
    const now = performance.now();
    const deltaTime = now - this.lastFrameTime;
    this.lastFrameTime = now;

    if (deltaTime > this.frameDropThreshold) {
      // Frame drop detected
      this.onFrameDropDetected?.(
        Math.floor(deltaTime / this.frameDropThreshold),
      );
    }

    return Math.min(60, 1000 / deltaTime);
  }

  public dispose(): void {
    this.activeMonitors.clear();
  }
}

class MemoryUsageMonitor {
  private memoryThreshold: number = 50 * 1024 * 1024; // 50MB

  public onMemoryThresholdExceeded?: (usage: number) => void;

  public getCurrentUsage(): number {
    if ("memory" in performance) {
      const memory = (performance as any).memory;
      const usage = memory.usedJSHeapSize;

      if (usage > this.memoryThreshold) {
        this.onMemoryThresholdExceeded?.(usage);
      }

      return usage;
    }
    return 0;
  }

  public dispose(): void {
    // Cleanup if needed
  }
}
